import React from 'react';
import { Box } from '@withjoy/joykit';
import { DrawerV2, DrawerV2Props } from '@withjoy/joykit/components/DrawerV2';
import { MobileProductQuickAddDrawerProps, MobileProductQuickAddDrawerRootProps } from './MobileProductQuickAddDrawer.types';
import { useMobileProductQuickAddDrawerProvider } from './MobileProductQuickAddDrawer.provider';
import { BaseProductDetails } from '../BaseProductDetails';
import { BuyForm } from '../../../ProductDetails/components/BuyForm';
import { useProductTile } from '../../useProductTile';
import { useGetCatalogProductDetailsQuery } from '../../../../api/queries/getCatalogProductDetails/getCatalogProductDetails';
import { MobileProductQuickAddContainerSkeleton, StyledCloseButton, styles } from './MobileProductQuickAddDrawer.styles';
import { EditCashFundCta } from '../EditCashFundCta';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useRegistryShopping } from '@apps/registry/admin/RegistryShopping.provider';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { useCatalogContext } from '../../../../state/Catalog';

const DRAWER_OVERRIDES: DrawerV2Props['overrides'] = {
  Content: {
    props: {
      opacity: '1 !important',
      background: '#FFFFFF !important',
      boxShadow: '0px 7px 27px rgba(44, 41, 37, 0.06), 0px 4px 16px rgba(0, 0, 0, 0.07)',
      borderRadius: '8px 8px 0px 0px',
      bottom: 0,
      overflowY: 'scroll',
      height: 'auto',
      maxHeight: 'calc(100vh - 90px)',
      paddingTop: 7,
    },
  },
  CloseButton: {
    props: {
      top: 6,
      right: 6,
    },
  },
};

export const MobileProductQuickAddDrawer: React.FC<MobileProductQuickAddDrawerProps> = (props: MobileProductQuickAddDrawerProps) => {
  const { catalogItemId } = props;
  const { isOpen, closeDrawer } = useMobileProductQuickAddDrawerProvider();
  const isMobile = useIsMobileScreen();
  const { isEnabled: isShoppingEnabled } = useRegistryShopping();
  // NOTE: we are using data instead of product from useGetCatalogProductDetailsQuery
  // to use the latest product data from query
  const {
    error,
    product,
    loading: isProductLoading,
  } = useGetCatalogProductDetailsQuery({
    catalogItemId: catalogItemId!,
    withPdpSections: false,
    withProductRating: false,
    withInventory: isShoppingEnabled,
    queryOptions: {
      batchMode: 'fast',
    },
  });

  return isMobile ? (
    <DrawerV2
      isOpen={isOpen && !error}
      key={catalogItemId || ''}
      anchor="bottom"
      size={1}
      useBackdrop={true}
      onClose={closeProps => {
        if (closeProps.closeSource === 'closeButton') {
          // TODO: update parent product tile with selected product
          closeDrawer();
        }
      }}
      overrides={DRAWER_OVERRIDES}
    >
      <StyledCloseButton
        __css={styles.closeButton}
        data-togglemethod="close"
      />
      <DrawerV2.Body
        maxHeight="100%"
        paddingBottom={[7, 0, 0]}
      >
        {isProductLoading ? (
          <MobileProductQuickAddDrawerSkeleton />
        ) : (
          product && (
            <MobileProductQuickAddDrawerRoot
              {...props}
              product={product}
            />
          )
        )}
      </DrawerV2.Body>
    </DrawerV2>
  ) : (
    <></>
  );
};

const MobileProductQuickAddDrawerRoot: React.FC<MobileProductQuickAddDrawerRootProps> = (props: MobileProductQuickAddDrawerRootProps) => {
  const {
    handleOnCashFundProductClick,
    handleDetailClick,
    handleDetailKeyPress,
    isAddLoading,
    productDisplayFields,
    handleOnViewMoreDimensionClick,
    changeActiveDimensionOption,
    swatchColorOptions,
    selectedProduct,
    setProductSelected,
    isTypeCashOrCharity,
    formatPrice,
    isProductInRegistry,
    onOpenEditGiftDialog,
  } = useProductTile(props);

  const { product, addCatalogItem, variant } = props;
  const { isOpenEditGiftDialog } = useCatalogContext();

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      justifyContent="flex-start"
    >
      <BaseProductDetails
        onDetailClick={handleDetailClick}
        onDetailKeyPress={handleDetailKeyPress}
        isAddLoading={isAddLoading}
        productDisplayFields={productDisplayFields}
        onViewMoreDimensionClick={handleOnViewMoreDimensionClick}
        onChangeActiveDimensionOption={changeActiveDimensionOption}
        swatchColorOptions={swatchColorOptions}
        variant={variant}
        isMobile
        isQuickAdd
        formatPrice={formatPrice}
        preload={true}
      />

      {isTypeCashOrCharity ? (
        <EditCashFundCta
          onClick={handleOnCashFundProductClick}
          marginBottom={5}
        />
      ) : (
        <>
          <BuyForm
            isMobile={true}
            showQuantity={!isTypeCashOrCharity}
            isQuickAddForm
            isAddingItem={false}
            showAllDimensions={true}
            relatedItems={product?.group?.items}
            onViewDetailsClick={handleDetailClick}
            product={product}
            addCatalogItem={addCatalogItem}
            setProductSelected={setProductSelected}
            selectedProduct={selectedProduct}
            dimensionSpacing={5}
            colorSwatchVariant="carousel"
            isItemAddedToRegistry={isProductInRegistry(props.catalogItemId || '')}
            setOpenGiftDialog={() => props.catalogItemId && onOpenEditGiftDialog(props.catalogItemId)}
            isOpenGiftDialog={isOpenEditGiftDialog}
          />
        </>
      )}
    </Box>
  );
};

export const MobileProductQuickAddDrawerSkeleton = () => {
  return (
    <MobileProductQuickAddContainerSkeleton isMobile={true}>
      <SkeletonThumbnail
        skeletonSize="0px"
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              paddingTop: '100%',
              backgroundColor: 'white',
            },
          },
        }}
      />
      <Box
        width="100%"
        marginTop={5}
      >
        <SkeletonText
          rows={1}
          width={100}
          skeletonHeight={3}
        />
        <SkeletonText
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          margin={4}
          enableVaryingRowWidth={true}
          rows={2}
          rowWidths={[80, 70]}
          skeletonHeight={14}
          spacing={'10px'}
        />
        <SkeletonText
          width={48}
          rows={1}
          skeletonHeight={'10px'}
        />
      </Box>
    </MobileProductQuickAddContainerSkeleton>
  );
};
